import { useState } from 'react';
import { Modal } from 'antd';
import Button from '../Button/Button';
import Input from '../Input/Input';
import Select from '../Select/Select';
import { t } from '../../i18n/i18n';
import './ExportDialog.scss';

const EMPTY = { start_date: '', end_date: '', status: '' };

/**
 * The "Export" button on a DataTable — the export endpoints take the same
 * `start_date` / `end_date` / `status` narrowing as the list they sit on, so
 * the range is asked for here rather than dumping every row.
 *
 * `onExport` is handed that range and owns the request, the file name and its
 * own failure message; the dialog stays open if it rejects.
 */
export default function ExportDialog({
  open,
  title = 'Export',
  statusOptions = [],
  busy,
  onExport,
  onClose,
}) {
  const [range, setRange] = useState(EMPTY);

  /** A table's "All" is `null` or a sentinel; the select keys its items by value. */
  const options = statusOptions.map((o) => ({ ...o, value: o.value ?? '' }));

  const invalid =
    range.start_date && range.end_date && range.start_date > range.end_date
      ? 'Start date is after the end date'
      : null;

  const submit = async () => {
    if (invalid) return;
    await onExport(range);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={460}
      destroyOnHidden
      className="export-dialog"
      rootClassName="export-dialog-root"
      aria-label={t(title)}
    >
      <div className="export-dialog__body">
        <h2 className="export-dialog__title">{t(title)}</h2>
        <p className="export-dialog__text">
          {t('Leave a field empty to export everything on that side of the range.')}
        </p>

        <div className="export-dialog__row">
          <Input
            type="date"
            variant="form"
            label="From"
            placeholder="Start date"
            value={range.start_date}
            onChange={(e) => setRange((prev) => ({ ...prev, start_date: e.target.value }))}
          />
          <Input
            type="date"
            variant="form"
            label="To"
            placeholder="End date"
            value={range.end_date}
            onChange={(e) => setRange((prev) => ({ ...prev, end_date: e.target.value }))}
            error={invalid}
          />
        </div>

        <Select
          variant="form"
          label="Status"
          placeholder="All"
          options={options}
          value={range.status}
          onChange={(e) => setRange((prev) => ({ ...prev, status: e.target.value }))}
        />

        <div className="export-dialog__actions">
          <Button variant="ghost" onClick={onClose}>
            {t('Cancel')}
          </Button>
          {/* Pill is 27.2h by default — sized to the 40h ghost it sits beside. */}
          <Button
            variant="pill"
            height={40}
            paddingInline={20}
            onClick={submit}
            disabled={Boolean(invalid) || busy}
          >
            {busy ? t('Exporting…') : t('Export')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
